import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { FC, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetCorrespondenceByIdQuery } from 'store/api/correspondence';
import { CorrespondenceForm } from 'types/correspondence';
import { useClassifiers } from 'utils/useClassifiers';
import { CORRESPONDENCE_FORM_CLASSIFIERS, correspondenceDtoToForm } from './correspondenceFormMapping';
import CorrespondencePageForm from './CorrespondencePageForm';

const ClassicCorrespondencePageEdit: FC = () => {
    const { id: correspondenceId } = useParams();
    const [initialData, setInitialData] = useState<CorrespondenceForm>();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: CORRESPONDENCE_FORM_CLASSIFIERS,
    });

    const { data: correspondence, isError, isLoading } = useGetCorrespondenceByIdQuery(correspondenceId ?? skipToken);

    // Lock initialData once classifiers are loaded and correspondence arrives.
    // Render-time guarded setState (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
    // — the `!initialData` guard prevents the loop, so RTK refetches do not reset RHF defaultValues.
    if (!initialData && !isLoadingClassifiers && correspondence) {
        setInitialData(correspondenceDtoToForm(correspondence, classifiers));
    }

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return <CorrespondencePageForm type="edit" initialData={initialData} classifiers={classifiers} />;
};

/**
 * In the new design a correspondence is edited section by section on its page, so its edit URL opens General there;
 * the section in edit holds the editing session that this URL holds in classic.
 */
const CorrespondencePageEdit: FC = () => {
    const isNew = useIsNewDesign();
    const { id: correspondenceId } = useParams();
    return isNew ? (
        <Navigate to={`/correspondence/${correspondenceId}?edit=general`} replace />
    ) : (
        <ClassicCorrespondencePageEdit />
    );
};

export default CorrespondencePageEdit;
